import React, {useState, useEffect, useCallback} from 'react'
import { connect, useDispatch } from 'react-redux';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { Link } from "react-router-dom";
import {
    getTypeActesAction,
    getFamilleActesAction,
    startLoader,
    createTypeActeAction,
    deleteDataAction,
    updateTypeActesAction,
    importTypeActeFileUploadAction,
    downloadTypeActeTemplateAction
} from '../../../../store/actions/ProviderActions';
import Select from 'react-select';
import swal from 'sweetalert';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { InputText } from 'primereact/inputtext';
import { Button as PButton } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { DataTableBase } from '../../DataTables/DataTableBase';
import { currentUser } from "../../../../store/selectors/CurrentUserSelector";
import { useTranslation } from "react-i18next";
import './TypeActe.css';

const TypeActe = (props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    
    // Initial states
    const initialFormState = {
        nameFr: '',
        nameEn: '',
        code: '',
        famille: ''
    };
    
    const initialErrorState = { name: '', famille: '' };

    // States
    const [formData, setFormData] = useState(initialFormState);
    const [file, setFile] = useState(null);
    const [errors, setErrors] = useState(initialErrorState);
    const [typeActesId, setTypeActesId] = useState('');
    const [addCard, setAddCard] = useState(false);
    const [updateMode, setUpdateMode] = useState(false);

    // Filter states
    const [filters, setFilters] = useState(null);
    const [globalFilterValue, setGlobalFilterValue] = useState('');

    // Helper to update form data
    const updateFormField = useCallback((field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    }, []);

    // Initialize filters
    const initFilters = useCallback(() => {
        setFilters({
            global: { value: null, matchMode: FilterMatchMode.CONTAINS },
            code: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
            'familleTypeActe.nameFr': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
            nameFr: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
            createdAt: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] },
        });
        setGlobalFilterValue("");
    }, []);

    // Reset form
    const resetForm = useCallback(() => {
        setFormData(initialFormState);
        setFile(null);
        setErrors(initialErrorState);
        setTypeActesId('');
        setUpdateMode(false);
    }, [initialFormState, initialErrorState]);

    // Create mode
    const createMode = useCallback(() => {
        resetForm();
        setAddCard(true);
    }, [resetForm]);

    // Download template handler
    const handleDownloadTemplate = useCallback(() => {
        dispatch(downloadTypeActeTemplateAction());
    }, [dispatch]);

    // Render header for data table
    const renderHeader = useCallback(() => {
        return (
            <div className="d-flex justify-content-between align-items-center">
                <PButton 
                    type="button" 
                    icon="pi pi-filter-slash"  
                    label={t('common.reinit')} 
                    outlined 
                    onClick={clearFilter}
                    className="me-2"
                    size="small"
                />
                <div className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText 
                        value={globalFilterValue} 
                        onChange={onGlobalFilterChange} 
                        placeholder={t('common.search')}
                        size="small"
                    />
                </div>
            </div>
        );
    }, [globalFilterValue, t]);

    // Clear filter
    const clearFilter = useCallback(() => {
        initFilters();
    }, [initFilters]);

    // Define columns
    const setColumns = useCallback(() => {
        return [
            {name: 'code', headerName: "Code", filterPlaceholder: t("common.filter-by-code"), style: { minWidth: '12rem' }, filter: true, bodyTemplate: null},
            {name: 'familleTypeActe.nameFr', headerName: t("provider.great-fam"), filterPlaceholder: t("provider.fam-filter"), style: { minWidth: '12rem' }, filter: true, bodyTemplate: null},
            {name: 'nameFr', headerName: t('common.name'), filterPlaceholder: t('common.filter-name'), style: { minWidth: '12rem' }, filter: true, bodyTemplate: null},
            {name: 'createdAt', filterField: 'createdAt', dataType: 'date', headerName: t("patient.created-on"), filterPlaceholder: t('facturation.create-date-filter'), style: { minWidth: '6rem' }, filter: true, bodyTemplate: datePaiementBodyTemplate, filterElement: dateFilterTemplate},
            {name: 'actions', headerName: 'Actions', filterPlaceholder: '', style: { minWidth: '6rem'}, filter: false, bodyTemplate: getActionsTemplate},
        ]
    }, [t]);

    // Global filter change handler
    const onGlobalFilterChange = useCallback((e) => {
        const value = e.target.value;
        if (!filters) {
            setGlobalFilterValue(value);
            return;
        }
        
        let _filters = { ...filters };
        
        // Additional safety check to ensure 'global' exists
        if (_filters['global']) {
            _filters['global'].value = value;
        } else {
            // If for some reason 'global' doesn't exist, create it
            _filters['global'] = { value: value, matchMode: FilterMatchMode.CONTAINS };
        } 
        setFilters(_filters);
        setGlobalFilterValue(value);
    }, [filters]);

    // Actions template
    const getActionsTemplate = useCallback((rowData) => {
        return (
            <div className="d-flex justify-content-center gap-1">
                {hasMenu('MENPARAMACTES') && canDo('MENPARAMACTES', 'UPDATEFAMILLEACTE') && (
                    <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => updateData(rowData)}
                        title={t("common.edit")}
                    >
                        <i className="fa fa-pencil"></i>
                    </button>
                )}
                {hasMenu('MENPARAMACTES') && canDo('MENPARAMACTES', 'DELETEFAMILLEACTE') && (
                    <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(rowData)}
                        title={t("common.delete")}
                    >
                        <i className="fa fa-trash"></i>
                    </button>
                )}
            </div>
        );
    }, [t]);

    // Delete handler
    const handleDelete = useCallback((rowData) => {
        swal({
            title: t("patient.delete-act-fam") + rowData.nameFr + " ?",
            text: t("patient.delete-message-fam"),
            icon: "warning",
            buttons: true,
            dangerMode: true,
        }).then((willDelete) => {
            if (willDelete) {
                const data = { type: "TYPEACTE", id: rowData.id };
                dispatch(startLoader());
                dispatch(deleteDataAction(data));
            }
        });
    }, [t, dispatch]);

    // Update data handler
    const updateData = useCallback((data) => {
        setFormData({
            nameEn: data.nameEn || '',
            nameFr: data.nameFr || '',
            code: data.code || '',
            famille: data.familleTypeActe?.id || ''
        });
        setTypeActesId(data.id);
        setUpdateMode(true);
        setAddCard(true);
    }, []);

    // Date filter template
    const dateFilterTemplate = useCallback((options) => {
        return (
            <Calendar 
                value={options.value} 
                onChange={(e) => options.filterCallback(e.value, options.index)} 
                dateFormat="dd/mm/yy" 
                placeholder="dd/mm/yyyy" 
                mask="99/99/9999" 
            />
        );
    }, []);

    // Date formatting for table
    const datePaiementBodyTemplate = useCallback((rowData) => {
        return formatDate(rowData.createdAt);
    }, []);

    const formatDate = useCallback((value) => {
        return value.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    }, []);

    // Permission checks
    const canDo = useCallback((menuCode, actionCode) => {
        const theMenu = props.currentUser.menus?.find(menu => menu.code === menuCode);
        return theMenu ? theMenu.actionMenus?.some(action => action.code === actionCode) : false;
    }, [props.currentUser.menus]);
    
    const hasMenu = useCallback((menuCode) => {
        return props.currentUser.menus?.some(menu => menu.code === menuCode) || false;
    }, [props.currentUser.menus]);

    // File selection handler
    const selectFile = useCallback((event) => {
        setFile(event.target.files[0]);
    }, []);

    // Save handler
    const onTypeActesSave = useCallback((e) => {
        e.preventDefault();
        
        // Handle file upload
        if(file) {
            dispatch(startLoader());
            dispatch(importTypeActeFileUploadAction(file));
            return;
        }
        
        // Validate form
        let error = false;
        const errorObj = { ...initialErrorState };
        
        if (!formData.nameFr.trim()) {
            errorObj.name = t("common.name-required");
            error = true;
        }
        
        if (!formData.famille) {
            errorObj.famille = t("provider.famille-required");
            error = true;
        }
        
        setErrors(errorObj);
        
        if (error) {
            swal('Oops', t('common.check-form-fields'), "error");
            return;
        }
        
        dispatch(startLoader());
        
        if (updateMode) {
            const obj = { 
                nameFr: formData.nameFr,
                nameEn: formData.nameEn, 
                code: formData.code, 
                familleTypeActeId: formData.famille 
            };
            dispatch(updateTypeActesAction(typeActesId, obj));
        } else {
            const obj = { 
                nameFr: formData.nameFr,
                nameEn: formData.nameEn, 
                code: formData.code, 
                famille: formData.famille 
            };
            dispatch(createTypeActeAction(obj));
        }
        
        setAddCard(false);
    }, [dispatch, file, formData, initialErrorState, t, typeActesId, updateMode]);

    // Load data on component mount
    useEffect(() => {
        initFilters();
        dispatch(startLoader());
        dispatch(getTypeActesAction()); 
        dispatch(getFamilleActesAction());    
    }, [dispatch, initFilters]);

    // Reset form when typeActes changes
    useEffect(() => {
        if (props.successMessage) {
            resetForm();
            setAddCard(false);
        }
    }, [props.successMessage]);


    return (
        <>
            {/* Header modernisé */}
            <div className="provider-type-header mb-0 form-head bg-white p-3 bt-5 border-top-solid">
                <div>
                    <h4 className="mb-1">{t("provider.act-types")}</h4>
                    <p className="text-muted mb-0 small">{t("common.manage-act-types")}</p>
                </div>
                <div className="d-flex gap-2">
                    {/* Download Template Button */}
                    <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={handleDownloadTemplate}
                    >
                        <i className="fa fa-download me-1"></i> {t("common.download-template")}
                    </button>
                    
                    {/* Create Button */}
                    {hasMenu('MENPARAMACTES') && canDo('MENPARAMACTES', 'CREATEFAMILLEACTE') && (
                        <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={createMode}
                        >
                            <i className="fa fa-plus-circle me-1"></i> {t("provider.new-fam-act")}
                        </button>
                    )}
                </div>
            </div>
            
            {/* Modal modernisée */}
            <Modal 
                show={addCard} 
                onHide={() => setAddCard(false)}
                centered
                backdrop="static"
            >
                <Modal.Header closeButton className="border-bottom">
                    <Modal.Title className="h5">
                        {updateMode ? t("provider.edit-act-type") : t("patient.create-act-fam")}
                    </Modal.Title>
                </Modal.Header>
                
                <Modal.Body className="p-4">
                    <form>
                        <div className="mb-3">
                            <label className="form-label">{t('common.code')}</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="0000"
                                value={formData.code}
                                disabled={props.showLoading || file}
                                onChange={(e) => updateFormField('code', e.target.value)}
                            />
                            <small className="text-muted">{t("common.code-optional")}</small>
                        </div>
                        
                        <div className="mb-3">
                            <label className="form-label">{t("provider.great-fam")} *</label>
                            <Select
                                className={`select-search ${errors.famille ? 'is-invalid' : ''}`}
                                value={props.familleActes
                                    ?.filter((opt) => opt.id === formData.famille)
                                    .map((opti) => ({ label: opti.nameFr, value: opti.id }))}
                                placeholder={t("provider.choose-great-family")}
                                onChange={(val) => updateFormField('famille', val?.value || '')}
                                isDisabled={props.showLoading || file}
                                options={props.familleActes?.map((opt) => ({ label: opt.nameFr, value: opt.id }))}
                                isClearable
                            />
                            {errors.famille && <div className="invalid-feedback d-block">{errors.famille}</div>}
                        </div>
                        
                        <div className="mb-3">
                            <label className="form-label">{t('common.label')} *</label>
                            <input
                                type="text"
                                className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                placeholder={t("provider.familleAct")}
                                value={formData.nameFr}
                                disabled={props.showLoading || file}
                                onChange={(e) => updateFormField('nameFr', e.target.value)}
                            />
                            {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                        </div>

                        <hr className="my-4" />
                        
                        <div className="mb-3">
                            <h6 className="text-primary mb-3">{t("common.import-csv")}</h6>
                            <div className="input-group">
                                <span className="input-group-text">CSV</span>
                                <input 
                                    type="file" 
                                    className="form-control" 
                                    onChange={selectFile}
                                    accept=".csv"
                                />
                            </div>
                            {file && (
                                <div className="alert alert-info mt-2 py-2">
                                    <small>{t("common.selected-file")}: <strong>{file.name}</strong></small>
                                </div>
                            )}
                        </div>
                    </form>
                </Modal.Body>
                
                <Modal.Footer className="border-top">
                    <button 
                        type="button" 
                        className="btn btn-outline-secondary"
                        onClick={() => setAddCard(false)}
                        disabled={props.showLoading}
                    >
                        {t("common.cancel")}
                    </button>
                    
                    {props.showLoading ? (
                        <Button variant="primary" disabled>
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                            />
                            <span className="ms-2">{t("common.saving")}</span>
                        </Button>
                    ) : (
                        <button 
                            type="button" 
                            className="btn btn-primary"
                            onClick={onTypeActesSave}
                        >
                            {updateMode ? t("common.update") : t("common.save")}
                        </button>
                    )}
                </Modal.Footer>
            </Modal>
            
            {/* DataTable */}
            <div className="card border-0 shadow-sm bg-white">
                <div className="card-body p-0">
                    <DataTableBase 
                        data={props.typeActes.length > 0 ? props.typeActes.map(acte => {
                                acte.createdAt = new Date(acte.createdAt);
                                acte.familleActeName = acte.familleTypeActe?.nameFr;
                                return acte;
                        }) : []}
                        emptyMessage={t("common.no-fam")} 
                        filters={filters}
                        globalFiltersFields={['code', 'familleTypeActe.nameFr', 'nameFr', 'createdAt']}
                        header={renderHeader()}
                        columns={setColumns()}
                        exportColumns={[
                            { title: 'Code', dataKey: 'code' },
                            { title: t("provider.great-fam"), dataKey: 'familleActeName' },
                            { title: t('common.name'), dataKey: 'nameFr' },
                            { title: t('patient.created-on'), dataKey: 'createdAt' },
                        ]}
                        rows={10} 
                        showExport={true}
                        exportFilename={"types-actes"}
                        loading={props.showLoading}
                        lazy={false}
                    />
                </div>
            </div>
        </>
    );
};

const mapStateToProps = (state) => {
    return {
        typeActes: state.provider.typeActes,
        familleActes: state.provider.familleActes,
        errorMessage: state.clientAuth.errorMessage,
        successMessage: state.clientAuth.successMessage,
        showLoading: state.provider.showLoading,
        currentUser: currentUser(state)
    };
};

export default connect(mapStateToProps)(TypeActe);